import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import type { TeamInvitation, TeamInvitationInsert, TeamInvitationUpdate } from '../../types/supabase'

// Get team invitations for a company. If companyId is not provided, fall back to the authenticated user's company.
export const useTeamInvitations = (companyId?: string) => {
  return useQuery({
    queryKey: ['teamInvitations', companyId ?? 'self'],
    queryFn: async () => {
      let targetCompanyId = companyId

      if (!targetCompanyId) {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) throw new Error('User not authenticated')

        targetCompanyId = user.user_metadata?.company_id || undefined

        if (!targetCompanyId) {
          // Fallback to users table if metadata is missing
          const { data: userRecord, error } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', user.id)
            .maybeSingle()

          if (error) throw error
          targetCompanyId = userRecord?.company_id || undefined
        }
      }

      if (!targetCompanyId) {
        return [] as TeamInvitation[]
      }

      const { data, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as TeamInvitation[]
    },
    enabled: true
  })
}

// Create a team invitation
export const useCreateTeamInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationData: TeamInvitationInsert) => {
      // Generate a unique token for the invitation
      const token = crypto.randomUUID()
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 7) // 7 days from now

      const { data, error } = await supabase
        .from('team_invitations')
        .insert({
          ...invitationData,
          token,
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single()

      if (error) throw error

      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-invite', {
        body: {
          invitationId: data.id
        }
      })

      if (sendError) {
        console.error('useCreateTeamInvitation: Failed to send invite email', sendError)
        throw sendError
      }

      if (sendResult && typeof sendResult === 'object' && 'error' in sendResult) {
        const message = (sendResult as { error: string }).error
        console.error('useCreateTeamInvitation: send-invite returned error', message)
        throw new Error(message)
      }

      return data as TeamInvitation
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvitations'] })
    }
  })
}

// Update team invitation
export const useUpdateTeamInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { id: string } & TeamInvitationUpdate) => {
      const { data, error } = await supabase
        .from('team_invitations')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as TeamInvitation
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvitations'] })
    }
  })
}

// Accept team invitation
export const useAcceptTeamInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ token, password, fullName }: { token: string; password: string; fullName?: string }) => {
      const { data, error } = await supabase.functions.invoke<{
        success?: boolean
        email?: string
        error?: string
        deviceSetup?: {
          required: boolean
          osType: string
          installerUrl: string
        }
      }>('accept-invite', {
        body: {
          intent: 'accept',
          token,
          password,
          fullName
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvitations'] })
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] })
    }
  })
}

export const useResendTeamInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { data, error } = await supabase.functions.invoke<{
        success?: boolean
        error?: string
      }>('send-invite', {
        body: { invitationId }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamInvitations'] })
    }
  })
}
